import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Tabs, TabsTrigger, TabsList, TabsContent } from '@/components/ui/tabs';
import { EventsTabContent } from './events-tab-content';
import {
  useWorkflowPanelStore,
  WorkflowPanelTab,
} from '@/stores/workflow-panel-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { NodeStatusEvent, WorkflowEventTypes } from '@/types/workflow-types';
import {
  GripVerticalIcon,
  Maximize2,
  X,
  PencilLine,
  ChevronDown,
  Settings2,
  ArrowLeft,
  Plus,
  CircleFadingArrowUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { NodeInputRenderer } from '../custom-node/generic-node/node-input-renderer';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import {
  CustomNode,
  NodeParam,
  NodeType,
  NodeStruct,
} from '@/types/node-types';
import { isCanvasDecorationNode } from '@/pages/workflow/helper/graph-analysis';
import { getAbsolutePosition } from '@/pages/workflow/helper/group-nodes';
import { cn } from '@/lib/utils';
import {
  CONSOLE_PANEL_MIN_WIDTH,
  CONSOLE_PANEL_MAX_WIDTH,
} from '@/constants/workflow-editor';
import { Separator } from '@/components/ui/separator';
import { WorkflowNodeList } from './workflow-node-list';
import { IconTile } from '../icon-tile';
import { getNodeIconColor } from '@/constants/node-styling';
import {
  useReactFlow,
  useViewport,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { useResizablePanel } from '@/hooks/use-resizable-panel';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FilesTabContent } from './files-tab-content';
import { EvalsTabContent } from './evals-tab-content';
import { ErrorsTabContent } from './errors-tab-content';
import { ScheduleConfigSection } from './schedule-sidebar';
import { NodeArtifactsSection } from './node-artifacts-section';
import { TriggersConfigSection } from './triggers-sidebar';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useOrgStore } from '@/stores/org-store';
import { LabelPicker, LabelBadges } from '@/components/label-picker';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useNotification } from '@/components/notifications';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { McpPublishTabContent } from './mcp-publish-tab';
interface ConsoleSidebarProps {
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  isConnected: boolean;
  headerButtons: React.ReactNode;
  panelGroupWidth?: number;
  onEditWorkflowInfo?: () => void;
  isLocked?: boolean;
  isWorkflowLocked?: boolean;
  /** Persisted workflow id for Evals tab API calls. */
  workflowId?: string | null;
  /**
   * Workflow run scope for eval results. Must be the agentic-backend
   * `session_id` (which is what the eval node forwards to platform-api as
   * `workflow_run_id`), NOT the platform `WorkflowRun.id`. Omit when no run
   * is selected — the Evals tab will then prompt the user to pick one.
   */
  workflowRunId?: string | null;
  /**
   * Source run's session_id when the current run is a rerun-from-node.
   * The Evals tab fetches results from this run as a fallback for any
   * eval node that was cached/skipped in the current run (since the
   * backend doesn't duplicate OpenSearch eval docs into the new
   * session for cached nodes). Null for fresh runs and pure past-run
   * views.
   */
  parentRunId?: string | null;
  /**
   * react_flow_generated_ids of nodes seeded as cached in the current
   * rerun-from-node run. Used by the Evals tab to know which eval
   * nodes to merge from `parentRunId` instead of expecting fresh
   * results in `workflowRunId`.
   */
  cachedEvalNodeIds?: Set<string>;
  /**
   * True while the workflow is actively executing on this canvas.
   * The Evals tab uses this to keep auto-polling indefinitely during a
   * live run (results trickle in as eval nodes finish) and only cap the
   * polling at 3 attempts once the workflow has terminated — that's
   * when a missing result indicates a real backend stall, not just
   * "still running".
   */
  isWorkflowExecuting?: boolean;
  isWorkflowPaused?: boolean;
}

const WORKFLOW_LOCK_EDITABLE_FIELDS = new Set([
  'task',
  'user_prompt',
  'request',
]);

const SHOW_ADVANCED_SETTINGS = false;

// Attributes each param group to its node when several are drag-selected.
const NodeConfigHeader: React.FC<{ struct: NodeStruct }> = ({ struct }) => {
  const iconColor = getNodeIconColor(struct.node_type, struct.icon_color);
  const useThemeColors = iconColor === null;
  const name = struct.user_config_name || struct.display_name;
  const description = struct.user_config_description ?? struct.description;
  return (
    <div className='flex items-center gap-3 p-4 border-b'>
      {struct.icon && (
        <IconTile
          icon={struct.icon}
          backgroundColor={iconColor || ''}
          iconColor={iconColor ? '#ffffff' : ''}
          size='large'
          style={
            !useThemeColors && iconColor
              ? {
                  background: `linear-gradient(to bottom, ${iconColor}, color-mix(in srgb, ${iconColor} 85%, black) )`,
                }
              : undefined
          }
          className={
            useThemeColors
              ? 'bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
              : ''
          }
          iconClassName={
            useThemeColors ? 'size-6 text-white dark:text-slate-950' : 'size-6'
          }
        />
      )}
      <div className='flex flex-col flex-1 min-w-0 gap-1 justify-center'>
        <TruncateTextWithTooltip
          text={name || 'Untitled Node'}
          className='text-base font-medium leading-tight text-foreground'
        />
        {description && (
          <div className='text-sm text-muted-foreground truncate leading-tight'>
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

export const tabTriggerClasses =
  'flex-initial h-7 px-3 py-1 rounded-md text-sm font-medium text-muted-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:border-transparent';

export const ConsoleSidebarLegacy: React.FC<ConsoleSidebarProps> = ({
  nodeStatuses,
  isConnected,
  headerButtons,
  panelGroupWidth: _panelGroupWidth = 1400,
  onEditWorkflowInfo,
  isLocked = false,
  isWorkflowLocked = false,
  workflowId = null,
  workflowRunId = null,
  parentRunId = null,
  cachedEvalNodeIds,
  isWorkflowExecuting = false,
  isWorkflowPaused = false,
}) => {
  const {
    activeTab,
    setActiveTab,
    mcpCode,
    mcpEndpoint,
    apiCode,
    hasMcpTabBeenViewed,
    hasApiTabBeenViewed,
    setFilteredNodeId,
    isMaximized,
    setMaximized,
    isConsolePanelOpen,
    panelSize,
    setPanelSize,
    highlightedFieldId,
    highlightedNodeId,
    setHighlightedField,
    nodePanelData,
  } = useWorkflowPanelStore();

  // Track which nodes have their advanced settings expanded (collapsed by default)
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState<
    Record<string, boolean>
  >({});

  // Handle expandAdvancedSettings from nodePanelData (triggered by FloatingControlBar's Advanced button)
  useEffect(() => {
    if (nodePanelData?.expandAdvancedSettings && nodePanelData?.nodeId) {
      setAdvancedSettingsOpen((prev) => ({
        ...prev,
        [nodePanelData.nodeId!]: true,
      }));
    }
  }, [nodePanelData]);

  // Calculate notification status internally
  const showNotification =
    (isConnected &&
      Object.values(nodeStatuses).some((statuses) =>
        statuses.some(
          (status) => status.event_type === WorkflowEventTypes.STATUS_UPDATE,
        ),
      )) ||
    (!!mcpCode && !hasMcpTabBeenViewed) ||
    (!!apiCode && !hasApiTabBeenViewed);

  const { isLoadingWorkflow, currentWorkflow } = useWorkflowStore();
  const { currentOrg } = useOrgStore();
  const {
    isLabelsEnabled,
    isWorkflowSchedulingEnabled,
    isCanvasA2aChatEnabled,
    isArtifactsEnabled,
    isTriggersEnabled,
  } = useLaunchDarkly();
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const {
    nodes,
    edges,
    groupedNodes,
    selectNode: storeSelectNode,
    deselectAllNodes,
    handleParameterChange: storeHandleParameterChange,
    updateNodeToCurrentVersion,
  } = useNodeStore();
  const updateAvailableNodes = useNodeStore((s) => s.updateAvailableNodes);

  const { setCenter } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const { zoom } = useViewport();

  const {
    unsupportedNodes,
    missingFiles,
    fieldErrors,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  } = useWorkflowValidationStore();
  const notification = useNotification();

  // Show selected node in Configure tab
  const nodeIdToNodeMap = useMemo(() => {
    const nodeIdToNodeArray = selectedNodeIds
      .map((nodeId) => {
        return [nodeId, nodes.find((node) => node.id === nodeId)] as const;
      })
      .filter(
        (entry): entry is readonly [string, CustomNode] =>
          entry[1] !== undefined &&
          entry[1].data?.struct !== undefined &&
          !isCanvasDecorationNode(entry[1]),
      );
    return Object.fromEntries(nodeIdToNodeArray) as Record<string, CustomNode>;
  }, [selectedNodeIds, nodes]);

  // Check if we have any nodes to configure (either hovered or selected)
  const hasNodesToShow = Object.keys(nodeIdToNodeMap).length > 0;
  const isSingleNode = Object.keys(nodeIdToNodeMap).length === 1;

  const firstSelectedNode = useMemo(() => {
    const entries = Object.entries(nodeIdToNodeMap);
    if (entries.length === 0) return null;
    const [nodeId, node] = entries[0];
    const struct = node.data.struct;
    const iconColor = getNodeIconColor(struct.node_type, struct.icon_color);
    return {
      nodeId,
      name: struct.user_config_name || struct.display_name,
      description: struct.user_config_description ?? struct.description,
      icon: struct.icon,
      iconColor,
      useThemeColors: iconColor === null,
      nodeSubType: struct.node_sub_type,
    };
  }, [nodeIdToNodeMap]);

  const isSelectedNodeUpdatable =
    !!firstSelectedNode &&
    updateAvailableNodes.some((n) => n.nodeId === firstSelectedNode.nodeId) &&
    !isLocked &&
    (!isWorkflowLocked ||
      firstSelectedNode.nodeSubType === 'file_input' ||
      firstSelectedNode.nodeSubType === 'text_input');

  const handleUpdateSelectedNode = useCallback(() => {
    if (!firstSelectedNode) return;
    const { nodeId } = firstSelectedNode;
    updateNodeToCurrentVersion(nodeId, groupedNodes);
    // Defer by one tick so the store update commits before ReactFlow
    // re-measures handle positions and edge anchors; otherwise edges
    // visually drift to stale coordinates.
    setTimeout(() => updateNodeInternals(nodeId), 0);
  }, [
    firstSelectedNode,
    groupedNodes,
    updateNodeToCurrentVersion,
    updateNodeInternals,
  ]);

  // Full id → node lookup used by Phase 2 eval filtering (O(1) vs O(n) find)
  const nodesById = useMemo(
    () => new Map(nodes.map((n) => [n.id, n])),
    [nodes],
  );

  const currentWorkflowLabels = currentWorkflow?.labels ?? [];

  // Get all workflow nodes (excluding canvas-only / non-configurable nodes)
  const workflowNodes = useMemo(() => {
    return nodes.filter(
      (node) =>
        node.type !== NodeType.SelectNode &&
        node.type !== NodeType.StickyNoteNode &&
        node.type !== NodeType.ArtifactNode &&
        node.type !== NodeType.GroupNode &&
        node.data?.struct !== undefined,
    );
  }, [nodes]);

  // All eval nodes on the canvas — powers the per-node filter in the Evals tab.
  // Also resolves each Evals node's upstream node display names from the
  // workflow edge graph (edge.target === this evals-node id) so the CSV
  // export can surface "which upstream node is being scored" alongside
  // the Evals node's own name.
  const allEvalNodes = useMemo(() => {
    const nameForNode = (id: string): string | null => {
      const n = nodesById.get(id);
      if (!n) return null;
      return (
        n.data?.struct?.user_config_name || n.data?.struct?.display_name || null
      );
    };

    // edge.target -> Set of edge.source node ids. Built once so each
    // eval node lookup is O(in-degree) rather than O(edges).
    const upstreamByTarget = new Map<string, Set<string>>();
    for (const e of edges) {
      if (!e?.target || !e?.source) continue;
      let bucket = upstreamByTarget.get(e.target);
      if (!bucket) {
        bucket = new Set();
        upstreamByTarget.set(e.target, bucket);
      }
      bucket.add(e.source);
    }

    return nodes
      .filter((n) => n.data?.struct?.node_sub_type === 'evals')
      .map((n) => {
        const params = n.data!.struct!.params ?? [];
        const multiSelectParam = params.find(
          (p: NodeParam) =>
            p.display_modal === 'MultiSelect' && Array.isArray(p.value),
        );
        const upstreamIds = upstreamByTarget.get(n.id) ?? new Set<string>();
        const upstreamNodeNames = Array.from(upstreamIds)
          .map(nameForNode)
          .filter((s): s is string => !!s);
        return {
          nodeId: n.id,
          name:
            n.data!.struct!.user_config_name ||
            n.data!.struct!.display_name ||
            'Evaluations',
          evalCount: Array.isArray(multiSelectParam?.value)
            ? (multiSelectParam!.value as unknown[]).length
            : 0,
          upstreamNodeNames,
        };
      });
  }, [nodes, edges, nodesById]);

  // When a single eval node (or a node connected to exactly one eval node)
  // is selected on the canvas, auto-select that node in the filter dropdown.
  const autoSelectedEvalNodeId = useMemo(() => {
    for (const id of selectedNodeIds) {
      const struct = nodeIdToNodeMap[id]?.data?.struct;
      if (!struct) continue;

      if (struct.node_sub_type === 'evals') return id;

      const connectedEvalNodes = edges
        .filter((e) => e.source === id || e.target === id)
        .map((e) => nodesById.get(e.source === id ? e.target : e.source))
        .filter(
          (n): n is CustomNode => n?.data?.struct?.node_sub_type === 'evals',
        );

      if (connectedEvalNodes.length === 1) return connectedEvalNodes[0].id;
    }
    return null;
  }, [selectedNodeIds, nodeIdToNodeMap, edges, nodesById]);

  // Determine which tabs should be shown based on available code
  const showScheduleTab =
    isWorkflowSchedulingEnabled && !!workflowId && workflowId !== 'new';
  // Same 'new' guard: a trigger needs a persisted workflow to bind to.
  const showTriggersSection =
    isTriggersEnabled && !!workflowId && workflowId !== 'new';

  const availableTabs = useMemo(() => {
    const tabs = ['configure', 'events', 'files', 'evals'];
    if (mcpCode) {
      tabs.push('mcp');
    }
    if (apiCode) {
      tabs.push('api');
    }
    if (
      unsupportedNodes.length > 0 ||
      missingFiles.length > 0 ||
      fieldErrors.length > 0 ||
      nodeListUnavailable ||
      multipleWorkflows !== null ||
      cyclicGraph !== null
    ) {
      tabs.push('errors');
    }
    return tabs;
  }, [
    mcpCode,
    apiCode,
    unsupportedNodes.length,
    missingFiles.length,
    fieldErrors.length,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  ]);

  // Check if MCP or API tabs are newly available (have notifications)
  const showMcpNotification = mcpCode && !hasMcpTabBeenViewed;
  const showApiNotification = apiCode && !hasApiTabBeenViewed;
  const showErrorsNotification =
    unsupportedNodes.length > 0 ||
    missingFiles.length > 0 ||
    fieldErrors.length > 0 ||
    nodeListUnavailable ||
    multipleWorkflows !== null ||
    cyclicGraph !== null;

  // Reusable component for code tabs
  const CodeTabContent = ({
    value,
    code,
    language = 'python',
  }: {
    value: string;
    code: string;
    language?: string;
  }) => (
    <TabsContent
      value={value}
      className='flex-1 flex flex-col mt-0 min-h-0 p-2 h-0 relative'
    >
      <CopyButton text={code} size='default' />
      <div className='flex-1 overflow-y-auto bg-zinc-100 dark:bg-zinc-800 p-4 rounded-md h-0 relative'>
        <SyntaxHighlighter code={code} language={language} />
      </div>
    </TabsContent>
  );

  // Handle active tab validation - switch to events if current tab is no longer available
  // Don't reset during workflow loading to avoid race conditions with URL params
  useEffect(() => {
    if (!isLoadingWorkflow && !availableTabs.includes(activeTab)) {
      setActiveTab('events');
    }
  }, [activeTab, availableTabs, setActiveTab, isLoadingWorkflow]);

  // Auto-hide errors tab when all errors are resolved
  const prevErrorCountRef = useRef<number>(0);
  useEffect(() => {
    const currentErrorCount =
      unsupportedNodes.length +
      missingFiles.length +
      fieldErrors.length +
      (nodeListUnavailable ? 1 : 0) +
      (multipleWorkflows !== null ? 1 : 0) +
      (cyclicGraph !== null ? 1 : 0);
    const previousErrorCount = prevErrorCountRef.current;

    if (
      previousErrorCount > 0 &&
      currentErrorCount === 0 &&
      activeTab === 'errors'
    ) {
      notification.success('All workflow issues have been resolved!');

      setActiveTab('configure');
    }

    prevErrorCountRef.current = currentErrorCount;
  }, [
    unsupportedNodes.length,
    missingFiles.length,
    fieldErrors.length,
    activeTab,
    setActiveTab,
    notification,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  ]);

  const focusedNodeId = selectedNodeIds[0] || null;

  // Resize handling via shared hook
  const { handleResizeStart, panelWidth } = useResizablePanel({
    panelSize,
    setPanelSize,
    edge: 'left', // Console sidebar has resize handle on left edge
    minSize: CONSOLE_PANEL_MIN_WIDTH,
    maxSize: CONSOLE_PANEL_MAX_WIDTH,
  });

  // Auto-filter Events tab by the focused node
  useEffect(() => {
    if (focusedNodeId) {
      setFilteredNodeId(focusedNodeId);
    } else {
      // Clear filter when no node is focused
      setFilteredNodeId(null);
    }
  }, [focusedNodeId, setFilteredNodeId]);

  // Calculate sidebar visibility state:
  // - Maximized: always show pinned panel
  // - Not maximized + node focused: show floating panel
  // - Not maximized + no node focused: show minimized button
  const showFloatingPanel = !isMaximized && isConsolePanelOpen;
  const showMinimized = !isMaximized && !isConsolePanelOpen;

  const handleNodeSelect = useCallback(
    (nodeId: string) => {
      // Find the node to get its position
      const node = workflowNodes.find((n) => n.id === nodeId);

      if (node) {
        // Grouped children store position relative to their parent group,
        // not absolute canvas coordinates — resolve the absolute position
        // so centering doesn't pan to the wrong spot.
        const absolutePosition = getAbsolutePosition(node, nodesById);
        setCenter(absolutePosition.x + 100, absolutePosition.y + 50, {
          zoom,
          duration: 500,
        });
      }

      storeSelectNode(nodeId);
    },
    [workflowNodes, nodesById, setCenter, zoom, storeSelectNode],
  );

  // Handler to clear node selection and go back to node list.
  // Avoids deselectAllNodes() which has a side effect of closing the
  // floating panel via setConsolePanelOpen(false).
  const handleBackToNodeList = useCallback(() => {
    const { nodes: currentNodes } = useNodeStore.getState();
    const updatedNodes = currentNodes.map((n) => ({ ...n, selected: false }));
    useNodeStore.setState({ nodes: updatedNodes, lastSelectedNodeId: null });
  }, []);

  // Handler to close panel and deselect all nodes
  const handleClosePanel = useCallback(() => {
    deselectAllNodes();
    setMaximized(false);
  }, [deselectAllNodes, setMaximized]);

  // Minimized state - floating button in top-right (when not maximized and no node focused)
  if (showMinimized) {
    return (
      <div className='absolute top-2 right-2 z-50'>
        <div
          className={cn(
            'px-2 py-[7px] rounded-xl shadow-sm relative',
            'bg-background border border-border',
            'flex items-center gap-2 flex-wrap',
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setMaximized(true)}
                className='shrink-0'
                aria-label='Maximize right panel'
              >
                <Maximize2 className='size-4 dark:text-white' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom'>
              <div className='text-center'>Maximize</div>
            </TooltipContent>
          </Tooltip>
          {/* Notification badge */}
          {showNotification && (
            <span className='absolute top-1 left-1 w-3 h-3 bg-red-500 rounded-full animate-pulse'></span>
          )}

          {!isWorkflowExecuting && (
            <Separator orientation='vertical' className='w-px h-5' />
          )}

          {headerButtons}
        </div>
      </div>
    );
  }

  // Shared panel content (used in both expanded and maximized states)
  const PanelContent = (
    <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as WorkflowPanelTab)}
        className='flex-1 flex flex-col gap-0 h-full'
      >
        <div className='flex items-center'>
          <TabsList className='flex justify-start gap-1 p-2 h-auto bg-background w-full rounded-lg'>
            <TabsTrigger value='configure' className={tabTriggerClasses}>
              Configure
            </TabsTrigger>
            <TabsTrigger value='events' className={tabTriggerClasses}>
              Events
            </TabsTrigger>
            <TabsTrigger value='files' className={tabTriggerClasses}>
              Files
            </TabsTrigger>
            <TabsTrigger value='evals' className={tabTriggerClasses}>
              Evals
            </TabsTrigger>
            {mcpCode && (
              <TabsTrigger
                value='mcp'
                className={`${tabTriggerClasses} relative`}
              >
                MCP
                {showMcpNotification && (
                  <span className='absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full animate-pulse'></span>
                )}
              </TabsTrigger>
            )}
            {apiCode && (
              <TabsTrigger
                value='api'
                className={`${tabTriggerClasses} relative`}
              >
                API
                {showApiNotification && (
                  <span className='absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full animate-pulse'></span>
                )}
              </TabsTrigger>
            )}
            {showErrorsNotification && (
              <TabsTrigger
                value='errors'
                className={`${tabTriggerClasses} relative`}
              >
                Errors
                <span className='absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-semibold bg-destructive text-destructive-foreground rounded-full'>
                  {unsupportedNodes.length +
                    missingFiles.length +
                    fieldErrors.length +
                    (nodeListUnavailable ? 1 : 0) +
                    (multipleWorkflows !== null ? 1 : 0) +
                    (cyclicGraph !== null ? 1 : 0)}
                </span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        <Separator />

        {/* Persistent header — switches between workflow and node info */}
        {hasNodesToShow && firstSelectedNode && isSingleNode ? (
          <div className='flex items-center gap-3 p-4 border-b shrink-0'>
            {firstSelectedNode.icon && (
              <IconTile
                icon={firstSelectedNode.icon}
                backgroundColor={firstSelectedNode.iconColor || ''}
                iconColor={firstSelectedNode.iconColor ? '#ffffff' : ''}
                size='large'
                style={
                  !firstSelectedNode.useThemeColors &&
                  firstSelectedNode.iconColor
                    ? {
                        background: `linear-gradient(to bottom, ${firstSelectedNode.iconColor}, color-mix(in srgb, ${firstSelectedNode.iconColor} 85%, black) )`,
                      }
                    : undefined
                }
                className={
                  firstSelectedNode.useThemeColors
                    ? 'bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
                    : ''
                }
                iconClassName={
                  firstSelectedNode.useThemeColors
                    ? 'size-6 text-white dark:text-slate-950'
                    : 'size-6'
                }
              />
            )}
            <div className='flex flex-col flex-1 min-w-0 gap-1 justify-center'>
              <TruncateTextWithTooltip
                text={firstSelectedNode.name || 'Untitled Node'}
                className='text-base font-medium leading-tight text-foreground'
              />
              {firstSelectedNode.description && (
                <div className='text-sm text-muted-foreground truncate leading-tight'>
                  {firstSelectedNode.description}
                </div>
              )}
            </div>
            {isSelectedNodeUpdatable && (
              <Button
                variant='ghost'
                size='sm'
                onClick={handleUpdateSelectedNode}
                className='shrink-0 h-8 gap-1.5 text-emerald-700 hover:bg-emerald-700 hover:text-white dark:text-emerald-300 dark:hover:bg-emerald-800/55 dark:hover:text-white'
              >
                <CircleFadingArrowUp className='size-4' />
                Update
              </Button>
            )}
          </div>
        ) : currentWorkflow ? (
          <div className='flex items-center gap-3 p-4 border-b shrink-0'>
            <IconTile
              icon='WorkflowIcon'
              backgroundColor=''
              iconColor=''
              size='large'
              className='bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
              iconClassName='size-6 text-white dark:text-slate-950'
            />
            <div className='flex flex-col flex-1 min-w-0 gap-1 justify-center'>
              <TruncateTextWithTooltip
                text={currentWorkflow.name || 'Untitled Workflow'}
                className='text-base font-medium leading-tight text-foreground'
              />
              {currentWorkflow.description && (
                <div className='text-sm text-muted-foreground truncate leading-tight'>
                  {currentWorkflow.description}
                </div>
              )}
            </div>
            {onEditWorkflowInfo && (
              <Button
                variant='ghost'
                size='icon'
                className='shrink-0 size-9'
                onClick={onEditWorkflowInfo}
              >
                <PencilLine className='size-4' />
              </Button>
            )}
          </div>
        ) : null}

        {/* Events Tab */}
        <TabsContent
          value='events'
          className='flex-1 flex flex-col mt-0 min-h-0'
        >
          <EventsTabContent
            nodeStatuses={nodeStatuses}
            onFocusNode={handleNodeSelect}
            isExecuting={isWorkflowExecuting}
            isPaused={isWorkflowPaused}
            isConnected={isConnected}
          />
        </TabsContent>

        {/* Configure Tab */}
        <TabsContent
          value='configure'
          className='flex-1 flex flex-col mt-0 min-h-0'
        >
          {!hasNodesToShow ? (
            <div className='flex flex-col h-full overflow-y-auto'>
              {/* Labels */}
              {isLabelsEnabled && currentWorkflow?.id && (
                <div className='p-4 border-b'>
                  <div className='flex flex-wrap items-center justify-between'>
                    <span className='text-sm font-medium'>Labels</span>
                    <LabelPicker
                      workflowId={currentWorkflow.id}
                      initialLabelIds={currentWorkflowLabels.map((l) => l.id)}
                      orgId={currentOrg?.orgid}
                      customTrigger={
                        <Button variant='ghost' size='sm'>
                          Add Label
                          <Plus className='size-4' />
                        </Button>
                      }
                      renderBadges={(labels, onRemove) =>
                        labels.length > 0 ? (
                          <div className='w-full mt-2'>
                            <LabelBadges labels={labels} onRemove={onRemove} />
                          </div>
                        ) : null
                      }
                    />
                  </div>
                </div>
              )}

              {/* Schedule */}
              {showScheduleTab && workflowId && (
                <ScheduleConfigSection
                  workflowId={workflowId}
                  workflowName={currentWorkflow?.name}
                  isVisible={activeTab === 'configure'}
                />
              )}

              {/* Triggers */}
              {showTriggersSection && workflowId && (
                <TriggersConfigSection
                  workflowId={workflowId}
                  workflowName={currentWorkflow?.name}
                  isVisible={activeTab === 'configure'}
                />
              )}

              {/* Node List */}
              <WorkflowNodeList
                nodes={workflowNodes}
                onNodeSelect={handleNodeSelect}
              />
            </div>
          ) : (
            // Show configurable parameters for each selected node
            <div className='text-sm text-foreground flex-1 overflow-y-auto'>
              {/* Back button to return to node list */}
              <div className='sticky top-0 z-10 bg-background p-1.5'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleBackToNodeList}
                  className='gap-2'
                  data-testid='back-to-node-list-button'
                >
                  <ArrowLeft className='size-4' />
                  <span>Configure Workflow</span>
                </Button>
              </div>
              <Separator />

              {Object.entries(nodeIdToNodeMap).map(([nodeId, node], index) => {
                const struct = node.data.struct;
                const isInputNode =
                  struct.node_sub_type === 'file_input' ||
                  struct.node_sub_type === 'text_input';

                // Helper function to check if a value is not empty
                // const isValueNotEmpty = (
                //   value: NodeParam['value'],
                // ): boolean => {
                //   if (value === undefined || value === null) {
                //     return false;
                //   }
                //   if (typeof value === 'string') {
                //     return value.trim() !== '';
                //   }
                //   if (Array.isArray(value)) {
                //     return value.length > 0;
                //   }
                //   // For numbers and booleans, any value is considered non-empty
                //   // (0 and false are valid values)
                //   return true;
                // };

                const configurableParams = struct.params.filter(
                  (field: NodeParam) => {
                    // Exclude advanced and hidden fields
                    if (field.advanced || field.hidden || !field.show) {
                      return false;
                    }
                    // For bindable fields: only include if value is not empty
                    // This supports legacy workflows that have bindable fields with values
                    // if (field.bindable_config) {
                    //   return isValueNotEmpty(field.value);
                    // }
                    return true;
                  },
                );
                const advancedParams = struct.params.filter(
                  (field: NodeParam) => field.advanced && !field.hidden,
                );
                const hasNoParams =
                  configurableParams.length === 0 &&
                  advancedParams.length === 0;

                return (
                  <div key={nodeId}>
                    {/* Per-node header so drag-selected nodes are each
                        labeled in the Configure sidebar. A divider
                        separates consecutive nodes. */}
                    {!isSingleNode && (
                      <>
                        {index > 0 && <Separator />}
                        <NodeConfigHeader struct={struct} />
                      </>
                    )}
                    {/* NODE BODY — equal vertical padding keeps the spacing
                        around each node header/divider even. */}
                    <div className='text-sm text-foreground px-1 py-4'>
                      {hasNoParams ? (
                        <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
                          No configurable parameters
                        </div>
                      ) : (
                        <>
                          {/* Regular configurable params */}
                          {configurableParams.map(
                            (field: NodeParam, index: number) => {
                              const isHighlighted =
                                highlightedNodeId === nodeId &&
                                highlightedFieldId === field.field_id;
                              return (
                                <React.Fragment
                                  key={`${nodeId}-${field.field_id}-${index}`}
                                >
                                  <NodeInputRenderer
                                    field={field}
                                    handleParameterChange={(
                                      fieldId,
                                      value,
                                      key,
                                    ) =>
                                      storeHandleParameterChange(
                                        nodeId,
                                        fieldId,
                                        value,
                                        key,
                                      )
                                    }
                                    nodeType={struct.node_type}
                                    nodeId={struct.react_flow_generated_id}
                                    workflowId={currentWorkflow?.id}
                                    isHighlighted={isHighlighted}
                                    onHighlightComplete={() =>
                                      setHighlightedField(null, null)
                                    }
                                    isDisabled={
                                      isLocked ||
                                      (isWorkflowLocked &&
                                        !isInputNode &&
                                        !WORKFLOW_LOCK_EDITABLE_FIELDS.has(
                                          field.field_id,
                                        ))
                                    }
                                  />
                                </React.Fragment>
                              );
                            },
                          )}

                          {/* NOTE: TEMPORARILY HIDING Advanced Settings section for now */}
                          {SHOW_ADVANCED_SETTINGS &&
                            advancedParams.length > 0 && (
                              <Collapsible
                                open={advancedSettingsOpen[nodeId] ?? false}
                                onOpenChange={(open) =>
                                  setAdvancedSettingsOpen((prev) => ({
                                    ...prev,
                                    [nodeId]: open,
                                  }))
                                }
                                className='mt-4 rounded-lg'
                              >
                                <CollapsibleTrigger asChild>
                                  <Button
                                    variant='ghost'
                                    className='w-full flex items-center justify-between px-3 py-2 h-auto hover:bg-muted/50'
                                  >
                                    <div className='flex items-center gap-2'>
                                      <Settings2 className='h-4 w-4 text-muted-foreground' />
                                      <span className='text-sm font-medium'>
                                        Advanced Settings
                                      </span>
                                      <span className='text-xs text-muted-foreground'>
                                        ({advancedParams.length})
                                      </span>
                                    </div>
                                    <ChevronDown
                                      className={cn(
                                        'h-4 w-4 text-muted-foreground transition-transform duration-200',
                                        advancedSettingsOpen[nodeId] &&
                                          'rotate-180',
                                      )}
                                    />
                                  </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent className='px-1 pb-3'>
                                  <Separator className='mb-3' />
                                  {advancedParams.map(
                                    (field: NodeParam, index: number) => {
                                      const isHighlighted =
                                        highlightedNodeId === nodeId &&
                                        highlightedFieldId === field.field_id;
                                      return (
                                        <NodeInputRenderer
                                          key={`${nodeId}-advanced-${field.field_id}-${index}`}
                                          field={{
                                            ...field,
                                            show: true,
                                          }}
                                          handleParameterChange={(
                                            fieldId,
                                            value,
                                            key,
                                          ) =>
                                            storeHandleParameterChange(
                                              nodeId,
                                              fieldId,
                                              value,
                                              key,
                                            )
                                          }
                                          nodeType={struct.node_type}
                                          nodeId={
                                            struct.react_flow_generated_id
                                          }
                                          workflowId={currentWorkflow?.id}
                                          isHighlighted={isHighlighted}
                                          onHighlightComplete={() =>
                                            setHighlightedField(null, null)
                                          }
                                          isDisabled={
                                            isLocked ||
                                            (isWorkflowLocked &&
                                              !isInputNode &&
                                              !WORKFLOW_LOCK_EDITABLE_FIELDS.has(
                                                field.field_id,
                                              ))
                                          }
                                        />
                                      );
                                    },
                                  )}
                                </CollapsibleContent>
                              </Collapsible>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {isCanvasA2aChatEnabled && isSingleNode && firstSelectedNode && (
                <>
                  {isArtifactsEnabled && workflowId && workflowId !== 'new' && (
                    <NodeArtifactsSection
                      workflowId={workflowId}
                      nodeId={firstSelectedNode.nodeId}
                    />
                  )}

                  {isLabelsEnabled && currentWorkflow?.id && (
                    <div className='border-t p-4'>
                      <div className='flex flex-wrap items-center justify-between'>
                        <span className='text-sm font-medium'>Labels</span>
                        <LabelPicker
                          workflowId={currentWorkflow.id}
                          initialLabelIds={currentWorkflowLabels.map(
                            (l) => l.id,
                          )}
                          orgId={currentOrg?.orgid}
                          customTrigger={
                            <Button variant='ghost' size='sm'>
                              Add Label
                              <Plus className='size-4' />
                            </Button>
                          }
                          renderBadges={(labels) =>
                            labels.length > 0 ? (
                              <div className='w-full mt-2'>
                                <LabelBadges labels={labels} />
                              </div>
                            ) : null
                          }
                        />
                      </div>
                    </div>
                  )}

                  {showScheduleTab && workflowId && (
                    <div className='border-t'>
                      <ScheduleConfigSection
                        workflowId={workflowId}
                        isVisible={activeTab === 'configure'}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </TabsContent>

        {/* MCP Tab — real gateway endpoint + connection snippet when the
            publish response carried one, legacy sample snippet otherwise */}
        {mcpCode &&
          (mcpEndpoint ? (
            <McpPublishTabContent mcpCode={mcpCode} mcpEndpoint={mcpEndpoint} />
          ) : (
            <CodeTabContent value='mcp' code={mcpCode} language='python' />
          ))}

        {/* API Tab */}
        {apiCode && (
          <CodeTabContent value='api' code={apiCode} language='python' />
        )}

        {/* Files Tab */}
        <TabsContent
          value='files'
          className='flex-1 flex flex-col mt-0 min-h-0'
        >
          <FilesTabContent
            nodes={workflowNodes}
            selectedNodeId={hasNodesToShow ? focusedNodeId : null}
            workflowName={currentWorkflow?.name}
          />
        </TabsContent>

        {/* Evals Tab */}
        <TabsContent
          value='evals'
          className='flex-1 flex flex-col mt-0 min-h-0 min-w-0 overflow-hidden'
        >
          <EvalsTabContent
            workflowId={workflowId}
            workflowRunId={workflowRunId}
            parentRunId={parentRunId}
            cachedEvalNodeIds={cachedEvalNodeIds}
            isWorkflowExecuting={isWorkflowExecuting}
            evalNodes={allEvalNodes}
            autoSelectedEvalNodeId={autoSelectedEvalNodeId}
          />
        </TabsContent>

        {/* Errors Tab */}
        {showErrorsNotification && (
          <TabsContent
            value='errors'
            className='flex-1 flex flex-col mt-0 min-h-0'
          >
            <ErrorsTabContent />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );

  // Floating panel state (intermediate) - when node is focused but not maximized
  if (showFloatingPanel) {
    return (
      <div
        className='absolute top-2 right-2 bottom-2 z-40'
        style={{ width: panelWidth }}
      >
        {/* Vertical Resize Line */}
        <div
          className='absolute left-0 top-2 bottom-2 w-px cursor-ew-resize bg-border z-10 flex items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2'
          onMouseDown={handleResizeStart}
        >
          <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
            <GripVerticalIcon className='size-2.5' />
          </div>
        </div>
        <div
          className={cn(
            'h-full rounded-xl shadow-lg',
            'bg-background border border-border',
            'flex flex-col overflow-hidden',
          )}
        >
          {/* Header */}
          <div className='p-2 shrink-0 border-b'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {/* Close panel button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClosePanel}
                      className='h-8 w-8'
                      aria-label='Close right panel'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>

                {!isWorkflowExecuting && (
                  <Separator orientation='vertical' className='h-5' />
                )}
              </div>
              {headerButtons}
            </div>
          </div>

          {PanelContent}
        </div>
      </div>
    );
  }

  // Maximized/Pinned state - full panel attached to edge with custom resize
  return (
    <div
      className='absolute top-0 right-0 bottom-0 z-40'
      style={{ width: panelWidth }}
    >
      {/* Resize handle on left edge */}
      <div
        className='absolute left-0 top-0 bottom-0 w-px cursor-ew-resize bg-border z-10 flex items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2'
        onMouseDown={handleResizeStart}
      >
        <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
          <GripVerticalIcon className='size-2.5' />
        </div>
      </div>
      <div className='h-full animate-in zoom-in-95 origin-top-right duration-200'>
        <div className='h-full bg-background border-l shadow-lg flex flex-col overflow-hidden'>
          {/* Header */}
          <div className='p-2 shrink-0'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {/* Close button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClosePanel}
                      className='h-8 w-8'
                      aria-label='Close right panel'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>

                {!isWorkflowExecuting && (
                  <Separator orientation='vertical' className='h-5' />
                )}
              </div>
              {headerButtons}
            </div>
          </div>

          {PanelContent}
        </div>
      </div>
    </div>
  );
};
